// Steuerung der Pille als Daten (Handoff Pille §6): Bereiche liefern nur Werte und Callbacks,
// gezeichnet wird alles von EINER generischen Widget-Komponente (Widget.tsx).

import type { Icon } from '@phosphor-icons/react';

/** Farben nach Funktion: Gold Anlegen/Hauptaktion · Blau Import/Links · Grün Export · Violett Filter ·
 *  Orange Bearbeiten · Rot Löschen · Grau Zurücksetzen/Archiv (+ Türkis, Indigo, Pink aus dem Prototyp). */
export type WidgetFarbe = 'gold' | 'blau' | 'gruen' | 'violett' | 'orange' | 'rot' | 'grau' | 'tuerkis' | 'indigo' | 'pink';

interface Basis {
  /** stabiler Schlüssel innerhalb der Steuerung */
  id: string;
  farbe: WidgetFarbe;
  icon: Icon;
  /** kleine Überschrift (ANLEGEN, FILTER …) */
  kicker: string;
  tooltip?: string;
  /** über die ganze Breite (sonst automatisch, siehe layout.ts) */
  voll?: boolean;
}

export interface AktionWidget extends Basis {
  typ: 'aktion';
  titel: string;
  unter?: string;
  onClick: () => void;
  /** läuft gerade (z. B. Speichern) – Knopf gesperrt, Icon dreht sich */
  laeuft?: boolean;
  gesperrt?: boolean;
}

export interface ExportFormat { id: string; label: string; unter?: string; onClick: () => void | Promise<void> }

export interface ExportWidget extends Basis {
  typ: 'export';
  titel: string;
  unter?: string;
  formate: readonly ExportFormat[];
  /** Umfang wie im ExportDropdown (Ansicht/Alle Daten) */
  umfang?: { wert: string; optionen: readonly { wert: string; label: string }[]; onChange: (w: string) => void };
}

export interface AuswahlOption { wert: string; label: string; gruppe?: string }

export interface AuswahlWidget extends Basis {
  typ: 'auswahl';
  wert: string;
  optionen: readonly AuswahlOption[];
  onChange: (wert: string) => void;
  /** Unterzeile; ohne Angabe „Kein Filter aktiv“ / „Filter aktiv“ gegenüber standard */
  unter?: string;
  /** Wert, der „kein Filter“ bedeutet */
  standard?: string;
  /** zusätzlich zwei Datumsfelder im Widget (z. B. Zeitraum von–bis), nur wenn sichtbar */
  zeitraum?: { sichtbar: boolean; von: string; bis: string; onVon: (v: string) => void; onBis: (v: string) => void };
}

export interface SchalterWidget extends Basis {
  typ: 'schalter';
  titel: string;
  an: boolean;
  onChange: (an: boolean) => void;
  /** Unterzeile; ohne Angabe „An“ / „Aus“ */
  unter?: string;
}

export interface SegmentWidget extends Basis {
  typ: 'segment';
  titel: string;
  wert: string;
  optionen: readonly { wert: string; label: string }[];
  onChange: (wert: string) => void;
}

export interface KennzahlWert { label: string; zahl: number | string; onClick?: () => void; aktiv?: boolean }

export interface KennzahlWidget extends Basis {
  typ: 'kennzahl';
  werte: readonly KennzahlWert[];
}

/** ‹ Wert › – z. B. Monat vor/zurück */
export interface SchrittWidget extends Basis {
  typ: 'schritt';
  titel: string;
  onZurueck: () => void;
  onVor: () => void;
  zurueckLabel?: string;
  vorLabel?: string;
}

export type WidgetDef = AktionWidget | ExportWidget | AuswahlWidget | SchalterWidget | SegmentWidget | KennzahlWidget | SchrittWidget;

/** Zwischenüberschrift im Raster (z. B. „Filter“) */
export interface Abschnitt { abschnitt: string }

export type SteuerungsEintrag = WidgetDef | Abschnitt;

export interface PillenSteuerung {
  /** Bereichsname („Tickets“) – Kopf „{titel} steuern“ und Ruhe-Pille */
  titel: string;
  suche?: { platzhalter: string; wert: string; onChange: (v: string) => void };
  /** schmaler Zurück-Knopf (Objekt-/Ticket-Detail) */
  zurueck?: { label: string; onClick: () => void };
  eintraege: readonly SteuerungsEintrag[];
}

export const istAbschnitt = (e: SteuerungsEintrag): e is Abschnitt => 'abschnitt' in e;
